import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { DesktopAdminLayout } from '@/components/common/admin/desktop/DesktopAdminLayout';
import { DesktopPageHeader } from '@/components/common/admin/desktop/DesktopPageHeader';
import { ReportReviewList } from '@/components/admin/corkBoard/ReportReviewList';
import { ReportDetailModal } from '@/components/admin/corkBoard/ReportDetailModal';
import { AdminSelectDropdown } from '@/components/common/admin/AdminSelectDropdown';
import { GlassCard } from '@/components/shared/redesign';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { CORK_BOARD_REPORT_STATUSES } from '@/constants/residents/corkBoardConstants';
import type { useReportReview } from '@/hooks/admin/useReportReview';
import type { CorkBoardReport, ReportStatus } from '@/types/residents/corkBoard';

interface DesktopReportReviewViewProps {
  review: ReturnType<typeof useReportReview>;
  detailVisible: boolean;
  selectedReport: CorkBoardReport | null;
  onOpenDetail: (report: CorkBoardReport) => void;
  onCloseDetail: () => void;
  onRemove: (report: CorkBoardReport) => void;
  onDismiss: (report: CorkBoardReport) => void;
  onWarn: (report: CorkBoardReport) => void;
}

export const DesktopReportReviewView: React.FC<DesktopReportReviewViewProps> = ({
  review,
  detailVisible,
  selectedReport,
  onOpenDetail,
  onCloseDetail,
  onRemove,
  onDismiss,
  onWarn,
}) => {
  const { redesign, semantic } = useAdminColors();

  const emptyMessage =
    review.statusFilter === 'pending'
      ? 'No pending reports. The community is looking good!'
      : 'No reports match the current filter.';

  return (
    <DesktopAdminLayout>
      <DesktopPageHeader
        title="Report Review"
        subtitle="Review and take action on reported community content."
      />

      <View style={styles.content}>
        <AdminSelectDropdown
          label="Status"
          options={CORK_BOARD_REPORT_STATUSES}
          selectedValue={review.statusFilter}
          onSelect={(value) => review.setStatusFilter(value as ReportStatus | 'all')}
          variant="anchored"
        />

        {review.error && (
          <View
            style={[
              styles.errorBanner,
              {
                backgroundColor: redesign.accent.redBg,
                borderColor: semantic.status.error,
              },
            ]}
          >
            <Text style={[styles.errorBannerText, { color: semantic.status.error }]}>
              Failed to load reports.
            </Text>
            <TouchableOpacity onPress={review.refetch}>
              <Text style={[styles.errorRetryText, { color: semantic.status.error }]}>
                Retry
              </Text>
            </TouchableOpacity>
          </View>
        )}

        <GlassCard padding={0} style={styles.card}>
          <View style={[styles.cardHeader, { borderBottomColor: redesign.card.border }]}>
            <Text style={[styles.cardTitle, { color: redesign.text.primary }]}>Reports</Text>
            <Text style={[styles.cardSubtitle, { color: redesign.text.secondary }]}>
              {review.totalCount} report{review.totalCount !== 1 ? 's' : ''}
            </Text>
          </View>

          <ReportReviewList
            reports={review.reports}
            isLoading={review.isLoading}
            isMutating={review.isMutating}
            onView={onOpenDetail}
            onRemove={onRemove}
            onDismiss={onDismiss}
            onWarn={onWarn}
            onRefresh={review.refetch}
            isRefreshing={review.isFetching}
            onLoadMore={review.loadMore}
            hasNextPage={review.hasNextPage}
            isFetchingMore={review.isFetching && review.reports.length > 0}
            emptyMessage={emptyMessage}
          />
        </GlassCard>
      </View>

      <ReportDetailModal
        visible={detailVisible}
        report={selectedReport}
        isMutating={review.isMutating}
        onRemove={onRemove}
        onDismiss={onDismiss}
        onWarn={onWarn}
        onClose={onCloseDetail}
      />
    </DesktopAdminLayout>
  );
};

const styles = StyleSheet.create({
  content: {
    flex: 1,
    gap: 16,
  },
  card: {
    flex: 1,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderBottomWidth: 1,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: '600',
  },
  cardSubtitle: {
    fontSize: 13,
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    borderWidth: 1,
  },
  errorBannerText: {
    fontSize: 13,
  },
  errorRetryText: {
    fontSize: 13,
    fontWeight: '600',
    textDecorationLine: 'underline',
  },
});
